import { useAppForm } from '@/hooks/app-form'
import { useApiMutation, useApiQuery } from '@/api/ApiQueryServiceProvider'
import { renderButtonControl } from '../../formRenderFunctions'
import { useSecurityContext } from '@/contexts/SecurityContext'

export type CompleteLiveProvisionComposerProps = {
  deviceNumber: number
  deviceProvisioningInfoNumber?: number
  networkCorridorName?: string
  networkSiteName?: string
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  showHeader?: boolean
  submitLabel?: string
}

export function CompleteLiveProvisionComposer(props: CompleteLiveProvisionComposerProps) {
  const {
    deviceNumber,
    deviceProvisioningInfoNumber,
    networkCorridorName,
    networkSiteName,
    onSuccess,
    onError,
    onCancel,
    showHeader = true,
    submitLabel,
  } = props
  const { userInfo } = useSecurityContext()

  const { data: deviceDataRaw, isLoading: isDeviceLoading } = useApiQuery('getDeviceByNumber', { deviceNumber })
  const deviceData = deviceDataRaw?.content

  const markProvisioningSuccess = useApiMutation('upsertDeviceProvisioningInfo', 'POST', {
    onError: (error) => {
      onError?.(error)
    },
  })

  const form = useAppForm({
    defaultValues: {},
    onSubmit: async () => {
      if (isDeviceLoading) return

      const existingProvisioningInfo: any = deviceData?.provisioningInfo || {}
      const resolvedProvisioningInfoNumber =
        deviceProvisioningInfoNumber || existingProvisioningInfo?.deviceProvisioningInfoNumber

      const provisioningInfoPayload: any = {
        deviceNumber,
        deviceProvisioningInfoNumber: resolvedProvisioningInfoNumber,
        deviceProvisioningStatus: 'success',
        deviceProvisioningType: 'live',
        deviceProvisionRequestedDate: existingProvisioningInfo.deviceProvisionRequestedDate,
        networkCorridorNumber: existingProvisioningInfo.networkCorridorNumber,
        networkSiteNumber: existingProvisioningInfo.networkSiteNumber,
        provisionedDeviceNumber:
          existingProvisioningInfo.provisionedDeviceNumber && existingProvisioningInfo.provisionedDeviceNumber >= 1000000000
            ? existingProvisioningInfo.provisionedDeviceNumber
            : deviceNumber,
      }

      // Backend validates these ids strictly, so only include valid values
      if (
        existingProvisioningInfo.provisionedDeviceNumber &&
        existingProvisioningInfo.provisionedDeviceNumber >= 1000000000
      ) {
        provisioningInfoPayload.provisionedDeviceNumber = existingProvisioningInfo.provisionedDeviceNumber
      }
      if (
        existingProvisioningInfo.deviceProvisionedRequestedBy &&
        existingProvisioningInfo.deviceProvisionedRequestedBy >= 1000000000
      ) {
        provisioningInfoPayload.deviceProvisionedRequestedBy = existingProvisioningInfo.deviceProvisionedRequestedBy
      } else if (userInfo?.userNumber && userInfo.userNumber >= 1000000000) {
        provisioningInfoPayload.deviceProvisionedRequestedBy = userInfo.userNumber
      }

      await markProvisioningSuccess.mutateAsync({
        deviceNumber,
        provisioningInfo: provisioningInfoPayload,
      })

      onSuccess?.()
    },
  })

  const isSubmitting = markProvisioningSuccess.isPending
  const corridor = networkCorridorName || deviceData?.provisioningInfo?.networkCorridorName || 'Unknown'
  const site = networkSiteName || deviceData?.provisioningInfo?.networkSiteName || 'Unknown'

  return (
    <form.AppForm>
      <div className="space-y-6">
        {showHeader && (
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">Confirm Live Provisioning Success</h2>
            <p className="text-sm text-muted-foreground">
              Mark the live provisioning request as successful.
            </p>
          </div>
        )}

        <div className="rounded-md border border-primary/20 bg-primary/5 p-4 space-y-2">
          <p className="text-sm font-medium">This action will:</p>
          <ul className="ml-4 space-y-1 text-sm text-muted-foreground">
            <li>- Set live provisioning status from <span className="font-semibold">requested</span> to <span className="font-semibold">success</span></li>
            <li>- Keep device status as <span className="font-semibold">live</span></li>
            <li>- Keep the selected live network as <span className="font-semibold">{corridor} - {site}</span></li>
          </ul>
        </div>

        <div className="pt-4 flex justify-center gap-3">
          {renderButtonControl({
            label: 'Cancel',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button',
          })}

          {renderButtonControl({
            label: isSubmitting ? 'Updating...' : (submitLabel ?? 'Mark as Success'),
            variant: 'default',
            type: 'submit',
          }, { SubmitButton: form.SubmitButton })}
        </div>
      </div>
    </form.AppForm>
  )
}
